<template>
  <div v-if="$page" id="home" class="home">
    <header class="header -home">
      <figure class="header__background">
        <img v-reveal="{ delay: 550 }" src="@/assets/images/home-header.svg" alt="" />
      </figure>

      <div class="header__content -home">
        <HighlightedTitle
          v-reveal="{ delay: 300 }"
          :title="$page.header.title"
          class="header__title -home"
        />

        <RawHtml v-reveal="{ delay: 400 }" :html="$page.header.description" />

        <nav v-reveal="{ delay: 500 }" class="header__nav">
          <RouterLink :to="{ name: 'themes' }" class="header__link button -link">{{
            $page.header.button
          }}</RouterLink>

          <RouterLink
            :to="{ name: 'program' }"
            class="header__link button -link -inline -mobile-hidden -dark"
          >
            <span>{{ $page.header.label }}</span>
            <Icon id="leaves" class="button__icon" />
          </RouterLink>
        </nav>

        <footer v-reveal="{ delay: 750 }" class="header__stats">
          <div v-for="(stat, statKey) in $page.header.items" :key="statKey" class="stat-box">
            <Statistic :stat="stat" />
          </div>
        </footer>
      </div>
    </header>

    <div style="position: relative; margin-top: 10rem">
      <Blob shape="a" />
    </div>

    <section id="programme" class="section home__program row">
      <div class="home__program-row">
        <figure v-reveal="{ delay: 100 }" class="section__background home__program-background">
          <img src="@/assets/images/home-tools.svg" alt="" />
        </figure>

        <Video mobile :video="$page.initiative.video" v-if="$page.initiative.video" />

        <div class="row__item -span-8">
          <HighlightedTitle
            v-reveal="{ delay: 100 }"
            :title="$page.initiative.title"
            class="section__title -home"
          />

          <RawHtml :html="$page.initiative.description" v-reveal="{ delay: 100 }" />

          <RouterLink v-reveal="{ delay: 100 }" :to="{ name: 'program' }" class="section__link">
            <span>{{ $page.initiative.label }}</span>
            <Icon id="leaves" />
          </RouterLink>
        </div>
      </div>

      <Video :video="$page.initiative.video" v-if="$page.initiative.video" />
    </section>

    <section id="actions" class="section -centered home__actions">
      <HighlightedTitle
        v-reveal="{ delay: 100 }"
        :title="$page.action.title"
        class="section__title -centered -home"
      />
      <RawHtml
        v-reveal="{ delay: 100 }"
        class="section__legend home__actions-text"
        :html="$page.action.description"
      />

      <RouterLink
        v-reveal="{ delay: 100 }"
        :to="{ name: 'program', hash: '#actions' }"
        class="section__link"
      >
        <span>{{ $page.action.label }}</span>
        <Icon id="leaves" />
      </RouterLink>

      <ul class="row -mobile home__actions__list">
        <li
          v-for="(item, idx) in $page.action.items"
          :key="idx"
          v-reveal="{ delay: idx * 75 }"
          class="home__actions__item"
        >
          <Image class="section__background -badge" :source="item.image" />
          <p class="home__actions__item__content">{{ item.name }}</p>
        </li>
      </ul>
    </section>

    <section id="outils" class="section row home__tools">
      <div class="row__item -span-7">
        <HighlightedTitle
          v-reveal="{ delay: 100 }"
          :title="$page.theme.title"
          class="section__title -home"
        />
        <p v-reveal="{ delay: 100 }" class="section__pad-right">{{ $page.theme.description }}</p>
        <RouterLink
          :to="{ name: 'themes' }"
          class="header__link button -link home__tools-button"
          style="margin-top: 41px"
        >
          {{ $page.theme.button }}
        </RouterLink>

        <div class="home__tools-list">
          <div
            v-for="(item, idx) in themes"
            :key="item.id"
            v-reveal="{ delay: idx * 100 }"
            class="home__tools-item"
          >
            <div class="home__tools-item-content">
              <RouterLink
                class="home__tools-item-link"
                :to="{ name: 'theme', params: { themeSlug: item.slug } }"
              >
                {{ item.title }}
              </RouterLink>
              <Icon id="leaves" />
            </div>
          </div>
        </div>
      </div>

      <img src="@/assets/images/home-program.svg" class="home__tools-background" alt="" />
    </section>

    <section id="projets" class="section row -reverse home__projects">
      <Blob shape="b" />

      <div class="row__item -span-6 -skip-1">
        <HighlightedTitle v-reveal :title="$page.project.title" class="section__title -home" />
        <RawHtml :html="$page.project.description" v-reveal />

        <RouterLink v-reveal :to="{ name: 'projects' }" class="button -link home__projects__link"
          >{{ $page.project.button }}
        </RouterLink>
      </div>

      <figure v-reveal="{ delay: 200 }" class="home__projects-image">
        <img src="@/assets/images/CRE-Accueil_Map.png" alt="" />
      </figure>
    </section>

    <section id="partenaires" class="section -mid-top row -align-end">
      <div class="row__item -span-6">
        <HighlightedTitle
          v-reveal="{ delay: 100 }"
          :title="$page.network.title"
          class="section__title -home"
        />
        <RawHtml v-reveal="{ delay: 100 }" :html="$page.network.description" />
        <div class="home__tools-item-content">
          <RouterLink class="home__tools-item-link" :to="{ name: 'involve' }">{{
            $page.network.label
          }}</RouterLink>
          <Icon id="leaves" />
        </div>
      </div>

      <Mockup v-reveal="{ delay: 200 }" class="section__background row__item -span-5 -skip-1" />
    </section>
  </div>
</template>

<script>
import Icon from '@/components/global/Icon.vue'
import Image from '@/components/global/Image.vue'
import RawHtml from '@/components/global/RawHtml.vue'
import Blob from '@/components/graphics/Blob.vue'
import Mockup from '@/components/graphics/Mockup.vue'
import HighlightedTitle from '@/components/partials/HighlightedTitle.vue'
import Statistic from '@/components/partials/Statistic.vue'
import Video from '@/components/partials/Video.vue'
import { PageMetaMixin } from '@/mixins'

export default {
  name: 'Home',
  beetPage: 'home',
  mixins: [PageMetaMixin],
  components: {
    RawHtml,
    Video,
    Blob,
    HighlightedTitle,
    Icon,
    Mockup,
    Image,
    Statistic,
  },
  computed: {
    themes() {
      const { themes = [] } = this.$page || {}
      return themes.map((themeId) => this.$beet.themes[themeId]).filter(Boolean)
    },
  },
}
</script>
